import { injectable, inject, LazyServiceIdentifier } from 'inversify';
import { Observable, Subject, shareReplay, map, catchError, throwError, of, from, firstValueFrom } from 'rxjs';
import { LightHttp } from './light-http.service';
import { ApiConfig } from './api-config';
import { AuthService } from './auth.service';
import { LoginService } from './login.service';
import { isAccessTokenStale, isAnonymousToken } from '../utils/token-expiry.util';
import { isImpersonating } from '../utils/impersonation-storage';

/** Admin v2: a refresh attempted while this tab views as a user. Never reaches the network. */
export class RefreshBlockedWhileImpersonatingError extends Error {
  status = 0;
  constructor() {
    super('Session refresh is disabled while viewing as a user.');
    this.name = 'RefreshBlockedWhileImpersonating';
  }
}

@injectable()
export class RefreshService {
  constructor(
    @inject(LightHttp) private http: LightHttp,
    @inject(ApiConfig) private apiConfig: ApiConfig,
    @inject(AuthService) private authService: AuthService,
    // Deferred identifier: refresh.service ⇄ login.service is a circular import,
    // so accessing the LoginService class at decoration time would TDZ in the
    // webpack build. LazyServiceIdentifier defers the class reference to plan time.
    @inject(new LazyServiceIdentifier(() => LoginService)) private loginService: LoginService,
  ) {}

  private inFlight$: Observable<string> | null = null;
  /** Set when a 401 arrives with NO rt_present marker => cookies disabled (FD4). Consumers stop auto-refreshing. */
  cookieSessionUnavailable = false;

  /** Emits when cookieSessionUnavailable flips true, so CrossTabAuthService can broadcast it. */
  readonly cookieUnavailable$ = new Subject<void>();

  /** Per-browser single-flight via Web Locks. Falls back to per-tab refresh() when locks unavailable. */
  refreshAcrossTabs(): Observable<string> {
    if (isImpersonating(this.authService.getToken())) {
      return throwError(() => new RefreshBlockedWhileImpersonatingError());
    }
    if (typeof navigator === 'undefined' || !('locks' in navigator)) return this.refresh();
    const acquire = async (): Promise<string> =>
      navigator.locks.request('xc-refresh', async (): Promise<string> => {
        const tok = this.authService.getToken();
        if (tok && !isAnonymousToken(tok) && !isAccessTokenStale(tok)) return tok; // a peer already refreshed
        return firstValueFrom(this.refresh());
      });
    return from(acquire());
  }

  /** R2-B2: the JS-readable marker the backend sets alongside the httpOnly __Secure-rt. */
  private hasPresenceCookie(): boolean {
    return typeof document !== 'undefined' && document.cookie.split('; ').some((c) => c.startsWith('rt_present='));
  }

  /**
   * FD3-FD5: single-flight, COOKIE-ONLY refresh (FE never holds the refresh token).
   * NOTE for callers: `inFlight$` is cleared only inside the terminal map/catchError
   * (deliberately NOT in finalize, which would also fire on a `firstValueFrom`
   * unsubscribe and defeat dedupe). Every caller MUST subscribe to the returned
   * observable — building the chain and dropping it unsubscribed would leave
   * `inFlight$` wedged. All current callers (scheduler, firstValueFrom, switchMap,
   * ensureFreshAccessToken) subscribe.
   */
  refresh(): Observable<string> {
    // Admin v2 impersonation: the refresh cookie is the ADMIN's. A refresh from this tab would mint an admin token
    // inside the impersonating tab (and the impersonation token is non-refreshable by contract) -- never send it.
    if (isImpersonating(this.authService.getToken())) {
      return throwError(() => new RefreshBlockedWhileImpersonatingError());
    }
    if (this.inFlight$) return this.inFlight$;
    this.inFlight$ = this.http
      .request<{ error: boolean; bearer: string }>(this.apiConfig.getRefreshEndpoint(), 'POST', {}, {
        withCredentials: true,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      .response$.pipe(
        map((res) => {
          this.authService.setAuthToken(res.bearer);
          this.inFlight$ = null;
          return res.bearer;
        }),
        catchError((err) => {
          this.inFlight$ = null;
          const status = (err && (err as any).status) || 0;
          if (status === 401) {
            if (!this.hasPresenceCookie()) {
              this.cookieSessionUnavailable = true;
              this.cookieUnavailable$.next();
            } else {
              this.loginService.clearLocalSession();
            }
          }
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.inFlight$;
  }

  /** Best-effort: refresh a stale token BEFORE an edge upload snapshots headers. Never blocks the upload. */
  ensureFreshAccessToken(url: string): Observable<void> {
    if (!LightHttp.refreshEnabled || this.cookieSessionUnavailable) return of(undefined);
    const token = this.authService.getToken();
    if (isImpersonating(token)) return of(undefined);
    if (!token || isAnonymousToken(token) || !isAccessTokenStale(token)) return of(undefined);
    return this.refresh().pipe(
      map(() => undefined),
      catchError(() => of(undefined)), // refresh failed → proceed anyway (upload may drop to anon attribution)
    );
  }
}
